import React from 'react';
import { Dropdown, Menu } from 'antd';
import { 
  Settings, 
  UserPlus, 
  Mail, 
  MessageSquare, 
  Mic, 
  FileText, 
  Upload, 
  CheckSquare, 
  CalendarDays, 
  Briefcase, 
  Layout
} from 'lucide-react';

const CallActionDropdownV2 = ({ record, permissions = [], onAction, rolePermissions }) => {
  const isLead = record.EntityName?.toLowerCase() === 'lead';
  const isEnquiry = record.EntityName?.toLowerCase() === 'enquiry';

  const hasPermission = (perm) => {
    if (!permissions || permissions.length === 0) return true; // Default to true if not specified
    return permissions.includes(perm);
  };

  const hasRolePermission = (permKey) => {
    if (!rolePermissions) return true; // Default to true if not specified/loading
    
    switch (permKey) {
      case 'addFollowUp':
        return rolePermissions.AddFollowup !== false;
      case 'email':
        return rolePermissions.SendMail !== false;
      case 'sms':
        return rolePermissions.SendSms !== false;
      case 'voice':
        return rolePermissions.SendVoice !== false;
      case 'addNote':
        return rolePermissions.AddNote !== false;
      case 'uploadDocument':
        return rolePermissions.UploadDocument !== false;
      case 'addTask':
        return rolePermissions.AddTask !== false;
      case 'addAppointment':
        return rolePermissions.AddAppointment !== false;
      case 'addOpportunity':
        return rolePermissions.AddDeal !== false;
      case 'addWebForm':
        return rolePermissions.AddWebform !== false;
      default:
        return true;
    }
  };

  const getMenuItems = () => {
    const items = [];

    if (isLead || !record.EntityName) {
      if (hasPermission('addFollowUp') && hasRolePermission('addFollowUp')) {
        items.push({
          key: 'addFollowUp',
          label: 'Add Followup',
          icon: <UserPlus size={16} className="text-blue-500" />,
        });
      }
      if (hasPermission('email') && hasRolePermission('email')) {
        items.push({
          key: 'email',
          label: 'Send Mail',
          icon: <Mail size={16} className="text-indigo-500" />,
        });
      }
      if (hasPermission('sms') && hasRolePermission('sms')) {
        items.push({
          key: 'sms',
          label: 'Send SMS',
          icon: <MessageSquare size={16} className="text-emerald-500" />,
        });
      }
      if (hasPermission('voice') && hasRolePermission('voice')) {
        items.push({
          key: 'voice',
          label: 'Send Voice',
          icon: <Mic size={16} className="text-orange-500" />,
        });
      }
      if (hasPermission('addNote') && hasRolePermission('addNote')) {
        items.push({
          key: 'addNote',
          label: 'Add Notes',
          icon: <FileText size={16} className="text-slate-500" />,
        });
      }
      if (hasPermission('uploadDocument') && hasRolePermission('uploadDocument')) {
        items.push({
          key: 'uploadDocument',
          label: 'Upload Document',
          icon: <Upload size={16} className="text-purple-500" />,
        });
      }
      if (hasPermission('addTask') && hasRolePermission('addTask')) {
        items.push({
          key: 'addTask',
          label: 'Add Task',
          icon: <CheckSquare size={16} className="text-cyan-500" />,
        });
      }
      if (hasPermission('addAppointment') && hasRolePermission('addAppointment')) {
        items.push({
          key: 'addAppointment',
          label: 'Add Appointment',
          icon: <CalendarDays size={16} className="text-rose-500" />,
        });
      }
      if (hasPermission('addOpportunity') && hasRolePermission('addOpportunity')) {
        items.push({
          key: 'addOpportunity',
          label: 'Add Deal',
          icon: <Briefcase size={16} className="text-amber-500" />,
        });
      }
      if (hasPermission('addWebForm') && hasRolePermission('addWebForm')) {
        items.push({
          key: 'addWebForm',
          label: 'Fill Webform',
          icon: <Layout size={16} className="text-teal-500" />,
        });
      }
    } else if (isEnquiry) {
      if (hasRolePermission('addNote')) {
        items.push({
          key: 'addNote',
          label: 'Add Note',
          icon: <FileText size={16} className="text-slate-500" />,
        });
      }
    }

    return items;
  };

  const menuItems = getMenuItems();

  if (menuItems.length === 0) return null;

  return (
    <Dropdown
      menu={{
        items: menuItems,
        onClick: ({ key }) => onAction(key, record),
      }}
      trigger={['click']}
      placement="bottomRight"
      overlayClassName="premium-action-dropdown"
    >
      <button className="p-2 rounded-lg transition-all hover:shadow-md hover:scale-110 bg-white border border-slate-100 text-slate-500 hover:text-blue-600">
        <Settings size={16} />
      </button>
    </Dropdown>
  );
};

export default CallActionDropdownV2;
